import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { State } from '../redux/redux.state';
import {
  BellyShape,
  ChestShape,
  CtaBehavior,
  CtaStyle,
  FontSettings,
  HipShape,
  LengthUnitOfMeasurement,
  WidgetResponse,
  WidgetState,
} from '../types/widget.state';
import { WidgetFeature } from '../types/widget/widgetFeature';
import client from '../clients/client';
import { mapWidgetResponseToWidgetState } from '../utils/map.widget.response.to.widget.state';

type ResponseState = State<WidgetState> & {
  getStatus: 'idle' | 'loading' | 'successful' | 'error';
  loadingMessage?: string;
};

export const initialState: ResponseState = {
  data: {
    showEasysizeWatermark: true,
    lengthUnit: 'cm',
    ctaBehavior: 'cart',
    ctaStyle: {
      borderRadius: 20,
      backgroundColor: '#0c0706',
      fontColor: '#ffffff',
      text: 'Add to cart',
    },
    fontSetting: {
      headerFontColor: '#17120F',
      headerFontSize: 24,
      headerFontWeight: '700',
      customHeaderFontUrl: '',
      bodyFontColor: '#000000',
      bodyFontSize: 14,
      bodyPadding: { horizontal: 12, vertical: 12 },
      bodyBorderHasRadius: true,
      bodyFontWeight: '400',
      customBodyFontUrl: '',
    },
    widgetBackground: '#FFFFFF',
    maleChestShape: {
      slim: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/chest/male_slim.png',
        imageTitle: 'Slim',
        imageUrl: 'https://cdn.easysize.me/images/shapes/chest/male_slim.png',
      },
      average: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/chest/male_average.png',
        imageTitle: 'Average',
        imageUrl:
          'https://cdn.easysize.me/images/shapes/chest/male_average.png',
      },
      broad: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/chest/male_broad.png',
        imageTitle: 'Broad',
        imageUrl: 'https://cdn.easysize.me/images/shapes/chest/male_broad.png',
      },
    },
    femaleChestShape: {
      slim: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/chest/female_slim.png',
        imageTitle: 'Slim',
        imageUrl: 'https://cdn.easysize.me/images/shapes/chest/female_slim.png',
      },
      average: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/chest/female_average.png',
        imageTitle: 'Average',
        imageUrl:
          'https://cdn.easysize.me/images/shapes/chest/female_average.png',
      },
      broad: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/chest/female_broad.png',
        imageTitle: 'Broad',
        imageUrl:
          'https://cdn.easysize.me/images/shapes/chest/female_broad.png',
      },
    },
    maleHipShape: {
      slim: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/hips/male_slim.png',
        imageTitle: 'Slim',
        imageUrl: 'https://cdn.easysize.me/images/shapes/hips/male_slim.png',
      },
      average: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/hips/male_average.png',
        imageTitle: 'Average',
        imageUrl: 'https://cdn.easysize.me/images/shapes/hips/male_average.png',
      },
      wide: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/hips/male_wide.png',
        imageTitle: 'Wide',
        imageUrl: 'https://cdn.easysize.me/images/shapes/hips/male_wide.png',
      },
    },
    femaleHipShape: {
      slim: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/hips/female_slim.png',
        imageTitle: 'Slim',
        imageUrl: 'https://cdn.easysize.me/images/shapes/hips/female_slim.png',
      },
      average: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/hips/female_average.png',
        imageTitle: 'Average',
        imageUrl:
          'https://cdn.easysize.me/images/shapes/hips/female_average.png',
      },
      wide: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/hips/female_wide.png',
        imageTitle: 'Wide',
        imageUrl: 'https://cdn.easysize.me/images/shapes/hips/female_wide.png',
      },
    },
    maleBellyShape: {
      flat: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/belly/male_flat.png',
        imageTitle: 'Flat',
        imageUrl: 'https://cdn.easysize.me/images/shapes/belly/male_flat.png',
      },
      average: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/belly/male_average.png',
        imageTitle: 'Average',
        imageUrl:
          'https://cdn.easysize.me/images/shapes/belly/male_average.png',
      },
      round: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/belly/male_round.png',
        imageTitle: 'Round',
        imageUrl: 'https://cdn.easysize.me/images/shapes/belly/male_round.png',
      },
    },
    femaleBellyShape: {
      flat: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/belly/female_flat.png',
        imageTitle: 'Flat',
        imageUrl: 'https://cdn.easysize.me/images/shapes/belly/female_flat.png',
      },
      average: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/belly/female_average.png',
        imageTitle: 'Average',
        imageUrl:
          'https://cdn.easysize.me/images/shapes/belly/female_average.png',
      },
      round: {
        defaultImageUrl:
          'https://cdn.easysize.me/images/shapes/belly/female_round.png',
        imageTitle: 'Round',
        imageUrl:
          'https://cdn.easysize.me/images/shapes/belly/female_round.png',
      },
    },
  },
  status: 'idle',
  getStatus: 'idle',
};

// Get the current settings
export const getWidgetFeatures = createAsyncThunk<WidgetResponse, string>(
  'widget/getFeature',
  async (shopId: string, { rejectWithValue }) => {
    try {
      const { data } = await client.get(`/features/widget/${shopId}`);
      return data;
    } catch (error: any) {
      const { status, data } = error.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

// Set the settings
export const setWidgetFeatures = createAsyncThunk<WidgetState, WidgetFeature>(
  'widget/setFeature',
  async (feature: WidgetFeature, { rejectWithValue }) => {
    try {
      const { data } = await client.post('/features/widget', feature);
      return data;
    } catch (error: any) {
      const { status, data } = error.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

export const widgetSlice = createSlice({
  name: 'widget',
  initialState,
  reducers: {
    resetWidgetStatus: (state) => {
      state.status = 'idle';
    },
    changeEasysizeWatermarkDisplayStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.data.showEasysizeWatermark = action.payload;
    },
    changeLengthUnit: (
      state,
      action: PayloadAction<LengthUnitOfMeasurement>
    ) => {
      state.data.lengthUnit = action.payload;
    },
    changeCtaStyle: (state, action: PayloadAction<CtaStyle>) => {
      state.data.ctaStyle = action.payload;
    },
    changeCtaBehavior: (state, action: PayloadAction<CtaBehavior>) => {
      state.data.ctaBehavior = action.payload;
    },
    changeWidgetBackground: (state, action: PayloadAction<string>) => {
      state.data.widgetBackground = action.payload;
    },
    changeWidgetFontSettings: (state, action: PayloadAction<FontSettings>) => {
      state.data.fontSetting = action.payload;
    },
    changeMaleChestShape: (state, action: PayloadAction<ChestShape>) => {
      state.data.maleChestShape = action.payload;
    },
    changeFemaleChestShape: (state, action: PayloadAction<ChestShape>) => {
      state.data.femaleChestShape = action.payload;
    },
    changeMaleHipShape: (state, action: PayloadAction<HipShape>) => {
      state.data.maleHipShape = action.payload;
    },
    changeFemaleHipShape: (state, action: PayloadAction<HipShape>) => {
      state.data.femaleHipShape = action.payload;
    },
    changeMaleBellyShape: (state, action: PayloadAction<BellyShape>) => {
      state.data.maleBellyShape = action.payload;
    },
    changeFemaleBellyShape: (state, action: PayloadAction<BellyShape>) => {
      state.data.femaleBellyShape = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(setWidgetFeatures.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(setWidgetFeatures.fulfilled, (state, action) => {
        state.status = 'successful';
      })
      .addCase(setWidgetFeatures.rejected, (state, action) => {
        state.status = 'error';
        state.errors = action.payload;
      })
      .addCase(getWidgetFeatures.pending, (state) => {
        state.getStatus = 'loading';
      })
      .addCase(getWidgetFeatures.fulfilled, (state, action) => {
        state.getStatus = 'successful';
        let widgetStateKeys = Object.keys(action.payload);
        if (widgetStateKeys.length === 0) {
          let widgetState = mapWidgetResponseToWidgetState({
            css: '',
            data: {},
          });
          state.data = widgetState;
        } else {
          let css = '';
          try {
            css = JSON.parse(action.payload.css);
          } catch (err) {
            css = action.payload.css;
          }
          state.data = mapWidgetResponseToWidgetState({
            css,
            data: action.payload.data,
          });
        }
      })
      .addCase(getWidgetFeatures.rejected, (state, action) => {
        state.getStatus = 'error';
        state.errors = action.payload;
      }),
});

export const {
  changeEasysizeWatermarkDisplayStatus,
  changeLengthUnit,
  changeCtaBehavior,
  changeCtaStyle,
  changeWidgetBackground,
  changeWidgetFontSettings,
  changeFemaleBellyShape,
  changeFemaleChestShape,
  changeFemaleHipShape,
  changeMaleBellyShape,
  changeMaleChestShape,
  changeMaleHipShape,
  resetWidgetStatus,
} = widgetSlice.actions;

export default widgetSlice.reducer;
